import React, { useEffect, useState } from 'react';
import QuotePricingEditor from './QuotePricingEditor';
import QuoteReview from '../Routes/QuoteReview';
import { packageTotal } from '../Routes/reportData';
import { defaultLines, getCatalog, useCatalog } from '../../services/catalog';
import './QuoteBuilder.css';

// a new package starts with the catalog's default lines (the warranty); `defaults` records that it got them
const newPackage = (id, items = getCatalog().items) => ({
  id,
  name: 'Custom Package',
  selected: true,
  services: defaultLines(items),
  defaults: items.length > 0,
  secondAttic: false,
  discount: null,
});

const totalText = (pkg) =>
  (pkg.services || []).length
    ? `$${packageTotal(pkg).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : 'No services yet';

/**
 * Quote flow: pick packages → price them → review & send.
 * This component owns every package (names, services, discounts), so Back between the pages —
 * or out to the attic page and in again — never loses work.
 *
 * initialState / onStateChange: the Inspection page keeps a copy (and saves it in the draft)
 * photos:     inspection photos for the pricing page's gallery
 * onSaveExit: save the draft and leave (hidden when there's nothing to save into)
 */
const QuoteBuilder = ({ inspection, initialState, onStateChange, photos, onSaveExit, onBack, onContinue }) => {
  const [currentPage, setCurrentPage] = useState(initialState?.currentPage || 'select'); // select | pricing | review
  const [packages, setPackages] = useState(initialState?.packages || [newPackage(1)]);
  // Summary / Detailed per quote — saved with the draft, so reopening a sent inspection shows what was sent
  const [formats, setFormats] = useState(initialState?.formats || {});
  const catalog = useCatalog();

  // first time on a phone, the catalog may arrive after the first package was made — give it the defaults then
  useEffect(() => {
    if (!catalog.items.length) return;
    setPackages((prev) =>
      prev.some((p) => p.defaults === false)
        ? prev.map((p) => (p.defaults === false ? { ...p, defaults: true, services: [...defaultLines(catalog.items), ...p.services] } : p))
        : prev
    );
  }, [catalog.items]);

  useEffect(() => {
    if (onStateChange) onStateChange({ currentPage, packages, formats });
  }, [currentPage, packages, formats, onStateChange]);

  const nextId = Math.max(...packages.map((p) => p.id), 0) + 1;
  const selected = packages.filter((p) => p.selected);
  const selectedCount = selected.length;

  // the pricing page edits the selected packages; fold every change (and any clones) back in
  const mergeFromEditor = (byId) => {
    setPackages((prev) => {
      const known = new Set(prev.map((p) => p.id));
      const updated = prev.map((p) => (byId[p.id] ? { ...p, ...byId[p.id], selected: p.selected } : p));
      const clones = Object.values(byId)
        .filter((p) => !known.has(p.id))
        .map((p) => ({ ...p, selected: true }));
      return [...updated, ...clones];
    });
  };

  const handleSendQuote = (sendData) => onContinue(sendData);

  // keyed by id, the shape the review page and the report use
  const selectedData = Object.fromEntries(selected.map((p) => [p.id, p]));

  if (currentPage === 'pricing') {
    return (
      <QuotePricingEditor
        packages={selected}
        photos={photos}
        atticSize={inspection && inspection.atticSize}
        onChange={mergeFromEditor}
        onSaveExit={onSaveExit}
        onBack={() => setCurrentPage('select')}
        onContinue={() => setCurrentPage('review')}
      />
    );
  }

  if (currentPage === 'review') {
    return (
      <QuoteReview
        inspection={inspection}
        packageData={selectedData}
        formats={formats}
        onFormatsChange={setFormats}
        onSaveExit={onSaveExit}
        onBack={() => setCurrentPage('pricing')}
        onSend={handleSendQuote}
        onDone={onBack}
      />
    );
  }

  return (
    <div className="quote-builder">
      {/* HEADER */}
      <div className="quote-header">
        <button className="quote-back-btn" onClick={onBack}>← Back</button>
        <div className="quote-header-content">
          <div className="quote-step-label">NEW QUOTE</div>
          <h2>Pick template(s)</h2>
        </div>
      </div>

      {/* CONTENT */}
      <div className="quote-content">
        {/* PACKAGES */}
        <div className="packages-section">
          {packages.map((pkg) => (
            <div key={pkg.id} className="package-box">
              <div className="package-header">
                <div className="package-checkbox">
                  <input
                    type="checkbox"
                    checked={pkg.selected}
                    onChange={() => setPackages(packages.map((p) => (p.id === pkg.id ? { ...p, selected: !p.selected } : p)))}
                    id={`pkg-${pkg.id}`}
                  />
                  <label htmlFor={`pkg-${pkg.id}`}></label>
                </div>
                <div className="package-info">
                  <h3>{pkg.name}</h3>
                </div>
                {packages.length > 1 && (
                  <button
                    className="btn-remove-pkg"
                    onClick={() => setPackages(packages.filter((p) => p.id !== pkg.id))}
                    title="Remove this package"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="package-price">{totalText(pkg)}</div>
            </div>
          ))}
        </div>

        {/* ADD PACKAGE BUTTON — a package you just added is one you want to quote, so it starts ticked */}
        <button className="btn-add-package" onClick={() => setPackages([...packages, newPackage(nextId)])}>
          + Add another quote
        </button>
      </div>

      {/* ACTION BUTTONS */}
      <div className="quote-actions">
        {onSaveExit && (
          <button className="btn-save-exit" onClick={onSaveExit}>
            Save & exit — send later
          </button>
        )}
        <button
          className={`btn-review ${selectedCount === 0 ? 'disabled' : ''}`}
          onClick={() => selectedCount > 0 && setCurrentPage('pricing')}
          disabled={selectedCount === 0}
        >
          Review {selectedCount} package{selectedCount !== 1 ? 's' : ''} →
        </button>
      </div>
    </div>
  );
};

export default QuoteBuilder;
